import React, { useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { IDays, IProductionCapacitySlot, ISchedule } from '@app/common';
import {
  applyLimit,
  buildOpenStarts,
  buildSections,
  CapacityMetric,
  clearDayLimits,
  copyDayLimits,
  CopyMode,
  ORDERS_METRIC,
  removeMetricLimits,
  slotKey,
  SlotKey,
  WEEK_DAYS,
  WeekCapacity,
} from './production-capacity-editor.helpers';
import { colors, fontFamily, ghostButton, hatch, primaryButton } from './editor.styles';
import { MetricTabs } from './MetricTabs';
import { AddCategoryPopover, TrackedTag } from './AddCategoryPopover';
import { EditorFeedback, SelectionToolbar } from './SelectionToolbar';
import { CapacityGrid, dayLabel } from './CapacityGrid';
import { CopyDayDialog } from './CopyDayDialog';
import { RemoveCategoryDialog } from './RemoveCategoryDialog';

interface ProductionCapacityEditorProps {
  index: number;
  onClose: () => void;
}

interface Snapshot {
  capacity: WeekCapacity;
  tags: TrackedTag[];
}

const listFr = (items: string[]) => (
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`
);

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value ?? null));

// Les modifications sont écrites dans le formulaire du shop : elles sont enregistrées avec la fiche
export const ProductionCapacityEditor = ({ index, onClose }: ProductionCapacityEditorProps) => {
  const { getValues, setValue } = useFormContext();

  const capacityPath = `productionCapacities.${index}.capacity`;
  const tagsPath = `productionCapacities.${index}.tags`;

  const capacity = (useWatch({ name: capacityPath }) || {}) as WeekCapacity;
  const tags = (useWatch({ name: tagsPath }) || []) as TrackedTag[];
  const capacityName = useWatch({ name: `productionCapacities.${index}.name` }) as string | undefined;
  const shopName = useWatch({ name: 'publicName' }) as string | undefined;
  const [takeAwaySchedule, deliverySchedule] = useWatch({ name: ['takeAwaySchedule', 'deliverySchedule'] }) as (ISchedule | undefined)[];

  const initial = useRef<Snapshot>({ capacity: clone(getValues(capacityPath)), tags: clone(getValues(tagsPath) || []) });

  const [metricKey, setMetricKey] = useState(ORDERS_METRIC);
  const [selected, setSelected] = useState<Set<SlotKey>>(new Set());
  const [bulkValue, setBulkValue] = useState('8');
  const [feedback, setFeedback] = useState<(EditorFeedback & { undo?: Snapshot }) | null>(null);
  const [menuDay, setMenuDay] = useState<IDays | null>(null);
  const [copySource, setCopySource] = useState<IDays | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);

  const metrics: CapacityMetric[] = useMemo(() => [
    { key: ORDERS_METRIC, label: 'Commandes', unit: 'cmd' },
    ...tags.map((tag) => ({ key: tag._id, label: tag.name, unit: tag.name.toLowerCase() })),
  ], [tags]);

  const metric = metrics.find((entry) => entry.key === metricKey) || metrics[0];

  const openStarts = useMemo(
    () => buildOpenStarts(capacity, [takeAwaySchedule, deliverySchedule]),
    [capacity, takeAwaySchedule, deliverySchedule],
  );

  const sections = useMemo(() => buildSections(openStarts), [openStarts]);

  const slotsByDay = useMemo(() => Object.fromEntries(
    WEEK_DAYS.map((day) => [day, new Map((capacity[day] || []).map((slot: IProductionCapacitySlot) => [slot.start, slot]))]),
  ) as Record<IDays, Map<number, IProductionCapacitySlot>>, [capacity]);

  const write = ({ capacity: nextCapacity, tags: nextTags }: Partial<Snapshot>) => {
    if (nextCapacity) setValue(capacityPath, nextCapacity, { shouldDirty: true });
    if (nextTags) setValue(tagsPath, nextTags, { shouldDirty: true });
  };

  const commit = (next: Partial<Snapshot>, message: string) => {
    const undo = { capacity: clone(capacity), tags: clone(tags) };

    write(next);
    setSelected(new Set());
    setMenuDay(null);
    setFeedback({ message, canUndo: true, undo });
  };

  const undo = () => {
    if (!feedback?.undo) return;

    write(feedback.undo);
    setFeedback({ message: 'Modification annulée', canUndo: false });
  };

  const toggleKeys = (keys: SlotKey[]) => {
    setSelected((current) => {
      const next = new Set(current);
      const isAllSelected = keys.length > 0 && keys.every((key) => next.has(key));

      keys.forEach((key) => (isAllSelected ? next.delete(key) : next.add(key)));

      return next;
    });

    setFeedback(null);
    setMenuDay(null);
  };

  const applyToSelection = (value: number | undefined) => {
    const keys = [...selected];

    if (!keys.length) return;

    const what = value === undefined ? 'sans limite' : value === 0 ? 'bloqué' : `${value} ${metric.unit}`;

    commit(
      { capacity: applyLimit(capacity, keys, metric.key, value) },
      `${metric.label} : ${what} sur ${keys.length} créneau${keys.length > 1 ? 'x' : ''}`,
    );
  };

  const selectDay = (day: IDays) => {
    setSelected(new Set([...openStarts[day]].map((start) => slotKey(day, start))));
    setFeedback(null);
    setMenuDay(null);
  };

  const clearDay = (day: IDays) => {
    commit(
      { capacity: clearDayLimits(capacity, day, metric.key) },
      `${dayLabel(day)} : limites ${metric.label.toLowerCase()} retirées`,
    );
  };

  const copyDay = ({ to, metrics: copiedMetrics, mode }: { to: IDays[]; metrics: string[]; mode: CopyMode }) => {
    if (!copySource) return;

    commit(
      { capacity: copyDayLimits(capacity, { from: copySource, to, metrics: copiedMetrics, mode, openStarts }) },
      `Capacité du ${dayLabel(copySource).toLowerCase()} copiée vers ${listFr(to.map((day) => dayLabel(day).toLowerCase()))}`,
    );

    setCopySource(null);
  };

  const addCategory = (tag: TrackedTag) => {
    commit({ tags: [...tags, tag] }, `${tag.name} ajoutée : sélectionnez des créneaux pour la limiter`);
    setMetricKey(tag._id);
    setIsAddOpen(false);
  };

  const removeCategory = (tagId: string) => {
    const removed = metrics.find((entry) => entry.key === tagId);

    commit(
      { tags: tags.filter((tag) => tag._id !== tagId), capacity: removeMetricLimits(capacity, tagId) },
      `${removed?.label} n’est plus limitée`,
    );

    if (metricKey === tagId) setMetricKey(ORDERS_METRIC);
    setPendingRemove(null);
  };

  const cancel = () => {
    write(initial.current);
    onClose();
  };

  const pendingMetric = metrics.find((entry) => entry.key === pendingRemove);

  return (
    <div
      style={{ position: 'relative', width: '100%', height: '100%', boxSizing: 'border-box', padding: '28px 32px 24px', color: colors.text, fontFamily, display: 'flex', flexDirection: 'column', gap: 18, textAlign: 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: colors.muted }}>
            {shopName ? `${shopName} · ` : ''}Capacités de production
          </div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: -0.5 }}>{capacityName || 'Capacité de production'}</h1>
          <div style={{ fontSize: 14, color: colors.muted }}>Nombre maximum par créneau de 15 min. Un créneau vide n'a pas de limite.</div>
        </div>

        <MetricTabs
          metrics={metrics}
          activeKey={metric.key}
          isAddOpen={isAddOpen}
          onPick={(key) => { setMetricKey(key); setFeedback(null); }}
          onRemove={(key) => { setPendingRemove(key); setIsAddOpen(false); setMenuDay(null); }}
          onToggleAdd={() => { setIsAddOpen(!isAddOpen); setMenuDay(null); }}>
          {isAddOpen && (
            <AddCategoryPopover trackedIds={tags.map((tag) => tag._id)} onAdd={addCategory} onClose={() => setIsAddOpen(false)} />
          )}
        </MetricTabs>
      </div>

      <SelectionToolbar
        selectedCount={selected.size}
        metricLabel={metric.label}
        bulkValue={bulkValue}
        feedback={feedback}
        onBulkChange={setBulkValue}
        onApply={applyToSelection}
        onDeselect={() => setSelected(new Set())}
        onUndo={undo} />

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {sections.length ? (
          <CapacityGrid
            sections={sections}
            openStarts={openStarts}
            slotsByDay={slotsByDay}
            metric={metric}
            selected={selected}
            copySource={copySource}
            menuDay={menuDay}
            onToggleKeys={toggleKeys}
            onToggleMenu={(day) => setMenuDay(menuDay === day ? null : day)}
            onCopyDay={(day) => { setCopySource(day); setMenuDay(null); setFeedback(null); }}
            onSelectDay={selectDay}
            onClearDay={clearDay} />
        ) : (
          <div style={{ height: '100%', minHeight: 240, borderRadius: 12, background: hatch, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, textAlign: 'center', padding: 24 }}>
            <span style={{ fontSize: 16, fontWeight: 700 }}>Aucun créneau ouvert</span>
            <span style={{ fontSize: 14, color: colors.muted, maxWidth: 460 }}>
              Les créneaux sont construits à partir des horaires à emporter et en livraison du restaurant. Renseignez-les et enregistrez la fiche pour définir des capacités.
            </span>
          </div>
        )}
      </div>

      <div style={{ paddingTop: 14, borderTop: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, fontSize: 12, color: colors.soft, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Faible</span>
            <div style={{ display: 'flex', gap: 2 }}>
              {[0.16, 0.28, 0.4, 0.52, 0.64].map((alpha) => (
                <span key={alpha} style={{ width: 22, height: 14, borderRadius: 3, background: `rgba(0,160,214,${alpha})` }} />
              ))}
            </div>
            <span>Élevée</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 22, height: 14, boxSizing: 'border-box', borderRadius: 3, border: '1px dashed rgba(253,255,252,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: colors.muted }}>–</span>
            <span>Sans limite</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 22, height: 14, borderRadius: 3, background: 'rgba(220,0,0,0.30)' }} />
            <span>Bloqué (0)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 22, height: 14, borderRadius: 3, background: 'repeating-linear-gradient(135deg, rgba(253,255,252,0.14) 0 3px, transparent 3px 6px)' }} />
            <span>Hors horaires</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" onClick={cancel} style={{ ...ghostButton, height: 44, padding: '0 22px', fontSize: 15 }}>Annuler</button>
          <button type="button" onClick={onClose} style={{ ...primaryButton, height: 44, padding: '0 26px', fontSize: 15 }}>Valider</button>
        </div>
      </div>

      {copySource && (
        <CopyDayDialog
          source={copySource}
          metrics={metrics}
          openStarts={openStarts}
          sections={sections}
          onConfirm={copyDay}
          onClose={() => setCopySource(null)} />
      )}

      {pendingMetric && (
        <RemoveCategoryDialog
          metric={pendingMetric}
          onConfirm={() => removeCategory(pendingMetric.key)}
          onCancel={() => setPendingRemove(null)} />
      )}
    </div>
  );
};
